import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/native';
import { useShallow } from 'zustand/react/shallow';
import { useMutation } from '@apollo/client';
import {
  CREATE_STYLIST_AVAILABILITY,
  UPDATE_STYLIST_AVAILABILITY,
} from '@/shared/mutations';
import useStore from '@/stores/useStore';
import { useGetUser } from '@/shared/hooks/useGetUser';
import AvailabilityStepper from './Stepper';
import {
  AvailabilityStep,
  STEPS_IN_ORDER,
} from '@/shared/constants/availability';
import { STEP_COMPONENTS } from '@/shared/constants/availabilitySteps';
import { StylistNylasData, StylistOpenHours } from '@/shared/types/generated';
import { AvailabilitySettings } from '@/shared/types/availability';
import {
  convertSettingsToRawOpenHours,
  getCurrentStep,
  getStepCompletionStatus,
} from './utils';
import SentryService from '@/services/sentry/Sentry.service';
import SectionLayout from './SectionLayout';
import useAvailabilityStore from '@/stores/useAvailabilityStore';
import { convertRawOpenHoursToSettings } from './utils';
import { ActivityIndicator } from 'react-native-paper';
import { green } from '@/shared/styles/colors';
import useBreakpoint from '@/shared/hooks/useBreakpoint';

interface AvailabilityManagerProps {
  /** When true, only the Schedule step is shown (no stepper, no summary). Used on mobile when calendar is already set up. */
  scheduleOnly?: boolean;
  schedulingData?: StylistNylasData | null;
  schedulingDataLoading: boolean;
  onRefetchSchedulingData?: () => Promise<unknown> | void;
}

const AvailabilityManager = ({
  scheduleOnly = false,
  schedulingData: schedulingDataProp,
  schedulingDataLoading,
  onRefetchSchedulingData,
}: AvailabilityManagerProps) => {
  const { user } = useGetUser();
  const { setSnackbarState } = useStore();
  const {
    setDailyOpenHoursSettings,
    setCalendarIds,
    setBookingCalendarId,
    setTimezone,
    dailyOpenHoursSettings,
    calendarIds,
    bookingCalendarId,
    timezone,
    setCalendars,
    setNylasConnections,
    setStylistId,
    stylistId,
    nylasConnections,
  } = useAvailabilityStore(
    useShallow((state) => ({
      setDailyOpenHoursSettings: state.setDailyOpenHoursSettings,
      setCalendarIds: state.setCalendarIds,
      setBookingCalendarId: state.setBookingCalendarId,
      setTimezone: state.setTimezone,
      dailyOpenHoursSettings: state.dailyOpenHoursSettings,
      calendarIds: state.calendarIds,
      bookingCalendarId: state.bookingCalendarId,
      timezone: state.timezone,
      setCalendars: state.setCalendars,
      setNylasConnections: state.setNylasConnections,
      setStylistId: state.setStylistId,
      stylistId: state.stylistId,
      nylasConnections: state.nylasConnections,
    })),
  );

  const [currentStep, setCurrentStep] = useState<AvailabilityStep>(
    AvailabilityStep.CalendarConnection,
  );
  const [completedSteps, setCompletedSteps] = useState<AvailabilityStep[]>([]);
  const [isAllCompleted, setIsAllCompleted] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const isNavigating = useRef(false);

  const { isDesktop } = useBreakpoint();

  useEffect(() => {
    if (!nylasConnections || nylasConnections.length === 0) {
      setCurrentStep(AvailabilityStep.CalendarConnection);
      setCompletedSteps([]);
      setIsAllCompleted(false);
      setIsEditing(false);
      isNavigating.current = false;
    }
  }, [nylasConnections]);

  const schedulingData = schedulingDataProp;

  useEffect(() => {
    if (!schedulingData) return;

    const scheduleConfig = schedulingData.scheduleConfigs?.[0];
    const rawOpenHours = schedulingData.openHours ?? [];

    const openHoursItems = rawOpenHours
      .filter((openHours): openHours is StylistOpenHours => !!openHours)
      .map(({ day, startTime, endTime }) => ({ day, startTime, endTime }));

    setDailyOpenHoursSettings(convertRawOpenHoursToSettings(openHoursItems));
    setStylistId(schedulingData?.stylistId);
    setCalendarIds(scheduleConfig?.availabilityCalendarIds || []);
    setBookingCalendarId(scheduleConfig?.bookingCalendarId || undefined);
    setTimezone(scheduleConfig?.timezone || undefined);
    setCalendars(schedulingData.calendars || []);
    setNylasConnections(schedulingData.nylasConnections || []);

    const completionStatus = getStepCompletionStatus(schedulingData);
    const serverCompletedSteps: AvailabilityStep[] = STEPS_IN_ORDER.filter(
      (step) => completionStatus[step],
    );

    const allStepsCompleted =
      serverCompletedSteps.length === STEPS_IN_ORDER.length;
    setIsAllCompleted(allStepsCompleted);

    if (scheduleOnly) {
      setCurrentStep(AvailabilityStep.Schedule);
      setCompletedSteps([...STEPS_IN_ORDER]);
      setIsEditing(true);
    } else if (isNavigating.current) {
      // While the user is actively stepping through the wizard, merge
      // server-confirmed completions with locally-tracked ones so the
      // stepper never regresses, but don't override currentStep.
      setCompletedSteps((prev) => {
        const merged = new Set([...prev, ...serverCompletedSteps]);
        return STEPS_IN_ORDER.filter((s) => merged.has(s));
      });
    } else {
      setCompletedSteps(serverCompletedSteps);
      if (!allStepsCompleted) {
        setCurrentStep(getCurrentStep(serverCompletedSteps));
      }
    }
  }, [schedulingData, scheduleOnly]);

  const [createStylistAvailability] = useMutation(CREATE_STYLIST_AVAILABILITY);
  const [updateStylistAvailability] = useMutation(UPDATE_STYLIST_AVAILABILITY);

  // Mark a step as complete and add it to completedSteps if not already there
  const markStepComplete = (step: AvailabilityStep) => {
    if (!completedSteps.includes(step)) {
      setCompletedSteps((prev) => [...prev, step]);
    }
  };

  // Navigate to a specific step if it's completed or the current step + 1
  const navigateToStep = (step: AvailabilityStep) => {
    const canGo =
      completedSteps.includes(step) ||
      step === currentStep ||
      (step === currentStep + 1 && completedSteps.includes(currentStep));
    if (canGo) {
      setIsEditing(true);
      setCurrentStep(step);
    }
  };

  // Handle completion of the current step
  const handleStepComplete = async () => {
    try {
      isNavigating.current = true;

      // DateSchedule already persists slots via setDateAvailability. Re-running
      // updateStylistAvailability here re-pushes weekly open hours (often empty
      // after the date-slot rollout) to Nylas and can fail independently of the
      // slot save — surfacing "availability settings" errors on every calendar save.
      if (currentStep !== AvailabilityStep.Schedule) {
        const currentSettings = {
          calendarIds,
          bookingCalendarId,
          timezone,
          openHours: convertSettingsToRawOpenHours(dailyOpenHoursSettings),
        };

        await handleAvailabilitySettingsUpdate(currentSettings);
      } else {
        await onRefetchSchedulingData?.();
      }

      markStepComplete(currentStep);

      // Move to the next step if not on the last step
      if (currentStep < AvailabilityStep.Schedule) {
        setCurrentStep((prev) => prev + 1);
      } else {
        setIsAllCompleted(true);
        setIsEditing(false);
        isNavigating.current = false;
      }
    } catch (error) {
      isNavigating.current = false;
      SentryService.captureError(
        `Error saving stylist id ${schedulingData?.stylistId} availability settings`,
        error,
      );
      setSnackbarState({
        message:
          'There was an issue saving your availability settings. Please try again.',
        severity: 'error',
        open: true,
      });
    }
  };

  const handleAvailabilitySettingsUpdate = async (
    stepData: Partial<AvailabilitySettings>,
  ) => {
    const input = {
      stylistId: stylistId,
      name: user?.firstName || 'Stylist',
      ...stepData,
    };

    if (schedulingData?.scheduleConfigs?.[0]?.id) {
      // Find the appropriate schedule config ids
      const thirtyMinConfig = schedulingData.scheduleConfigs.find(
        (config) => config?.eventType === 'THIRTY_MINUTES',
      );
      const sixtyMinConfig = schedulingData.scheduleConfigs.find(
        (config) => config?.eventType === 'SIXTY_MINUTES',
      );

      if (thirtyMinConfig?.id && sixtyMinConfig?.id) {
        await updateStylistAvailability({
          variables: {
            input,
          },
        });
        await onRefetchSchedulingData?.();
      }
    } else {
      await createStylistAvailability({
        variables: { input },
      });
      await onRefetchSchedulingData?.();
    }
  };

  // Render the current step
  const renderCurrentStep = () => {
    const StepComponent = STEP_COMPONENTS[currentStep];
    return StepComponent
      ? StepComponent({
          schedulingData,
          isAllCompleted,
          onSaveAvailability: handleStepComplete,
        })
      : null;
  };

  if (schedulingDataLoading) {
    return (
      <LoadingContainer>
        <ActivityIndicator size="large" color={green} />
      </LoadingContainer>
    );
  }

  const showSummary = isAllCompleted && !isEditing && !scheduleOnly;

  return (
    <Container>
      {!scheduleOnly && (
        <StepperContainer>
          <AvailabilityStepper
            currentStep={showSummary ? null : currentStep}
            completedSteps={completedSteps}
            onStepPress={navigateToStep}
          />
        </StepperContainer>
      )}

      {showSummary ? (
        <SectionLayout
          heading="Your Schedule Is Live!"
          description="Clients can now see when you're available — and actually book you. Go on, update your schedule anytime you need to mix it up."
          buttonText="Update Schedule"
          onButtonPress={() => {
            setIsEditing(true);
            setCurrentStep(AvailabilityStep.Schedule);
          }}
          titleStyle={{ maxWidth: isDesktop ? '100%' : 250 }}
          descriptionStyle={{
            maxWidth: isDesktop ? 460 : 265,
            marginBottom: 8,
          }}
          isButtonDisabled={false}
          isStep={false}
        >
          <></>
        </SectionLayout>
      ) : (
        <StepContainer>{renderCurrentStep()}</StepContainer>
      )}
    </Container>
  );
};

const Container = styled.View({
  flex: 1,
  width: '100%',
  justifyContent: 'space-between',
});

const StepperContainer = styled.View({
  marginBottom: 24,
});

const StepContainer = styled.View({
  flex: 1,
  width: '100%',
});

const LoadingContainer = styled.View({
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
});

export default AvailabilityManager;
